(() => {
  const config = window.ElitePCS || {};
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const preferredResolutions = ['1080p', '1440p', '4k'];
  const preferredSettings = ['low', 'medium', 'high', 'ultra'];

  const keyOf = (value) => (value || '').toString().trim().toLowerCase();
  const labelOf = (value) => keyOf(value) === '4k' ? '4K' : (value || '').toString().trim().replace(/\b\w/g, (char) => char.toUpperCase());
  const escapeHtml = (value) => (value || '').toString().replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);
  const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const handleize = (value) => keyOf(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const hiddenGameHandles = new Set((config.pcsByGameHiddenGames || '').toString().split(/[\n,;]+/).map(handleize).filter(Boolean));

  const safeUrl = (value, fallback = '') => {
    const url = (value || '').toString().trim();
    if (!url) return fallback;
    if (url.charAt(0) === '/') return url;

    try {
      const parsed = new URL(url, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : fallback;
    } catch {
      return fallback;
    }
  };

  const directImageUrl = (value) => {
    const url = (value || '').toString().trim();
    if (!url || /^[a-z0-9_. -]+\.(avif|webp|png|jpe?g|gif)$/i.test(url)) return '';
    if (url.startsWith('/') || /^https?:\/\//i.test(url) || url.startsWith('//')) return safeUrl(url, '');
    return '';
  };

  const bareImageFilename = (value) => {
    const filename = (value || '').toString().trim();
    return /^[a-z0-9_. -]+\.(avif|webp|png|jpe?g|gif)$/i.test(filename) ? filename : '';
  };

  const shopifyFileUrl = (modal, filename) => {
    const base = (modal && modal.dataset.shopifyFilesBase || '').trim();
    const cleanFilename = (filename || '').toString().trim().replace(/^\/+/, '');

    if (!base || !cleanFilename) return '';

    return base + cleanFilename.split('/').map(encodeURIComponent).join('/');
  };

  const gameImageSlugs = (game) => {
    const slug = handleize(game.slug || game.name);
    const aliases = {
      warzone: 'call-of-duty-warzone',
      'call-of-duty': 'call-of-duty-warzone',
      'gta-v': 'grand-theft-auto-v',
      'forza-horizon': 'forza-horizon-4',
      'microsoft-flight-simulator': 'microsoft-flight-simulator-2020'
    };
    const mapped = aliases[slug];
    const slugs = [];

    if (mapped) slugs.push(mapped);
    if (slug) slugs.push(slug);

    return slugs.filter((candidate, index) => candidate && slugs.indexOf(candidate) === index);
  };

  const isGameHidden = (game) => {
    if (!hiddenGameHandles.size) return false;
    const candidates = [
      ...gameImageSlugs(game),
      handleize(game.name),
      handleize(game.slug)
    ];

    return candidates.some((slug) => hiddenGameHandles.has(slug));
  };

  const gameImageFilename = (game) => game.shopify_file
    || game.shopifyFile
    || game.image_file
    || game.imageFile
    || game.image_filename
    || game.imageFilename
    || game.file
    || game.filename
    || bareImageFilename(game.image || game.imageUrl || game.image_url || game.poster || game.posterUrl || game.poster_url)
    || '';

  const gameImageCandidates = (game, modal) => {
    const candidates = [];
    const imageFields = [
      game.image,
      game.imageUrl,
      game.image_url,
      game.poster,
      game.posterUrl,
      game.poster_url,
      game.thumbnail,
      game.thumbnailUrl,
      game.thumbnail_url
    ];

    imageFields.forEach((value) => {
      const direct = directImageUrl(value);
      if (direct) candidates.push(direct);
    });

    const explicitFilename = gameImageFilename(game);
    if (explicitFilename) candidates.push(shopifyFileUrl(modal, explicitFilename));

    gameImageSlugs(game).forEach((slug) => {
      [
        `${slug}.webp`,
        `${slug}.png`,
        `${slug}.jpg`,
        `${slug}.jpeg`,
        `game-${slug}.webp`,
        `game-${slug}.png`
      ].forEach((filename) => candidates.push(shopifyFileUrl(modal, filename)));
    });

    return Array.from(new Set(candidates.filter(Boolean)));
  };

  const endpointForHandle = (value, handle) => {
    const endpoint = (value || '').toString().trim();
    if (!endpoint || !handle) return '';

    try {
      const parsed = new URL(endpoint, window.location.origin);
      if (!['http:', 'https:'].includes(parsed.protocol)) return '';

      const cleanPath = parsed.pathname.replace(/\/$/, '');
      if (!cleanPath.includes('/api/fps/')) {
        parsed.pathname = cleanPath.endsWith('/api/fps')
          ? `${cleanPath}/${encodeURIComponent(handle)}`
          : `${cleanPath}/api/fps/${encodeURIComponent(handle)}`;
      }

      parsed.search = '';
      return parsed.href;
    } catch {
      return '';
    }
  };

  const readCachedJson = (key, maxAge) => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!cached || !cached.savedAt || Date.now() - cached.savedAt > maxAge) return null;
      return cached.value;
    } catch {
      return null;
    }
  };

  const writeCachedJson = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
    } catch {
      // Ignore storage failures. The live fetch has already succeeded.
    }
  };

  const ordered = (values, preferred) => {
    const map = new Map();
    values.filter(Boolean).forEach((value) => map.set(keyOf(value), value));

    return [
      ...preferred.filter((key) => map.has(key)).map((key) => map.get(key)),
      ...Array.from(map.entries()).filter(([key]) => !preferred.includes(key)).map(([, value]) => value)
    ];
  };

  const axesForGame = (game) => {
    const fps = game && game.fps && typeof game.fps === 'object' ? game.fps : {};
    const resolutions = [];
    const settings = [];

    Object.entries(fps).forEach(([resolution, presetMap]) => {
      resolutions.push(resolution);
      Object.keys(presetMap || {}).forEach((preset) => settings.push(preset));
    });

    return {
      resolutions: ordered(resolutions, preferredResolutions),
      settings: ordered(settings, preferredSettings)
    };
  };

  const fpsValue = (game, resolution, preset) => {
    const fps = game && game.fps && typeof game.fps === 'object' ? game.fps : {};
    const resolutionKey = Object.keys(fps).find((key) => keyOf(key) === keyOf(resolution));
    if (!resolutionKey) return null;

    const presetMap = fps[resolutionKey] || {};
    const presetKey = Object.keys(presetMap).find((key) => keyOf(key) === keyOf(preset));
    const value = presetKey ? Number(presetMap[presetKey]) : NaN;

    return Number.isFinite(value) ? value : null;
  };

  const allFpsRows = (game) => {
    const axes = axesForGame(game);
    const rows = [];

    axes.resolutions.forEach((resolution) => {
      axes.settings.forEach((preset) => {
        const value = fpsValue(game, resolution, preset);
        if (value !== null) rows.push({ resolution, preset, value });
      });
    });

    return rows;
  };

  const bestFpsRow = (game) => allFpsRows(game).reduce((best, row) => {
    if (!best || row.value > best.value) return row;
    return best;
  }, null);

  const featuredFpsRow = (game) => {
    const preferredCombos = [
      ['1440p', 'high'],
      ['1440p', 'ultra'],
      ['1080p', 'high'],
      ['1080p', 'ultra'],
      ['4k', 'high'],
      ['4k', 'ultra'],
      ['1440p', 'medium'],
      ['1080p', 'medium']
    ];

    for (const [resolution, preset] of preferredCombos) {
      const value = fpsValue(game, resolution, preset);
      if (value !== null) return { resolution, preset, value };
    }

    return bestFpsRow(game);
  };

  const performanceSummary = (row) => {
    if (!row) return {
      tone: 'muted',
      label: 'FPS pending',
      fps: 'Data pending',
      compact: 'FPS data pending'
    };

    const resolution = labelOf(row.resolution);
    if (row.value >= 120) return {
      tone: 'excellent',
      label: `Great at ${resolution}`,
      fps: `${row.value}+ FPS`,
      compact: `${row.value}+ FPS`
    };

    if (row.value >= 60) return {
      tone: 'good',
      label: `Good at ${resolution}`,
      fps: `${row.value}+ FPS`,
      compact: `${row.value}+ FPS`
    };

    return {
      tone: 'low',
      label: 'Playable',
      fps: `${row.value} FPS`,
      compact: `${row.value} FPS`
    };
  };

  const gameImageMarkup = (game, size = 96, modal = null) => {
    const [image, ...fallbacks] = gameImageCandidates(game, modal);
    if (!image) {
      return '<span class="elite-compatible-games-modal__game-placeholder" aria-hidden="true"></span>';
    }

    const fallbackAttribute = fallbacks.length
      ? ` data-image-fallbacks="${escapeHtml(JSON.stringify(fallbacks))}"`
      : '';

    return `<img src="${escapeHtml(image)}" alt="${escapeHtml(game.name || 'Game')}" loading="lazy" width="${size}" height="${size}" data-compatible-game-image${fallbackAttribute}>`;
  };

  const normaliseConfiguration = (configuration = {}, modal) => ({
    processor: configuration.processor || configuration.cpu || modal.dataset.productCpu || '',
    graphics_card: configuration.graphics_card || configuration.graphicsCard || configuration.gpu || modal.dataset.productGpu || '',
    memory: configuration.memory || configuration.ram || modal.dataset.productRam || '',
    storage: configuration.storage || '',
    power_supply: configuration.power_supply || configuration.powerSupply || configuration.psu || '',
    cooling: configuration.cooling || configuration.cpu_cooler || configuration.cpuCooler || '',
    case: configuration.case || configuration.case_name || configuration.caseName || '',
    operating_system: configuration.operating_system || configuration.operatingSystem || configuration.os || ''
  });

  const cheapestMatchForGame = (game, modal) => {
    const recommended = Array.isArray(game.recommended_products) ? game.recommended_products : [];
    const raw = game.cheapest_match || game.cheapestMatch || recommended[0] || {};
    const rawHandle = raw.handle ? `/products/${raw.handle}` : '';
    const url = safeUrl(raw.url || rawHandle, modal.dataset.productUrl || window.location.pathname);
    const priceLabel = raw.price_label || raw.priceLabel || formatMoney(raw.price);
    const configuration = normaliseConfiguration(raw.configuration || raw.specs || raw, modal);

    return {
      title: raw.title || modal.dataset.productTitle || 'ElitePCS Gaming PC',
      url,
      priceLabel: priceLabel || modal.dataset.productPrice || '',
      fps: raw.fps && typeof raw.fps === 'object' ? raw.fps : game.fps,
      vsCurrent: raw.vs_current || raw.vsCurrent || null,
      configuration,
      why: raw.why || `This is the lowest-cost configuration currently available in the FPS data for ${game.name || 'this game'}.`
    };
  };

  const fpsSource = (fps) => ({ fps: fps && typeof fps === 'object' ? fps : {} });

  const gameRowMarkup = (game, index, modal) => {
    const match = cheapestMatchForGame(game, modal);
    const featured = featuredFpsRow(fpsSource(match.fps));
    const summary = performanceSummary(featured);
    const searchText = [
      game.name,
      game.slug,
      game.category
    ].filter(Boolean).join(' ');

    return (
      '<div class="elite-compatible-games-modal__row" role="row" data-compatible-game-row data-compatible-game-title="' + escapeHtml(game.name || 'Game') + '" data-compatible-game-search="' + escapeHtml(searchText) + '">' +
        '<span class="elite-compatible-games-modal__game" role="cell">' +
          gameImageMarkup(game, 96, modal) +
          '<strong>' + escapeHtml(game.name || 'Game') + '</strong>' +
        '</span>' +
        '<span class="elite-compatible-games-modal__performance" role="cell">' +
          '<strong class="is-' + summary.tone + '">' + escapeHtml(summary.label) + '</strong>' +
          '<small>' + escapeHtml(summary.compact) + '</small>' +
        '</span>' +
        '<span class="elite-compatible-games-modal__action-cell elite-compatible-games-modal__action-cell--fps" role="cell">' +
          '<button class="elite-compatible-games-modal__action elite-compatible-games-modal__action--secondary" type="button" data-compatible-game-fps="' + index + '">' +
            '<strong>FPS Info</strong>' +
            '<small>All settings</small>' +
          '</button>' +
        '</span>' +
        '<span class="elite-compatible-games-modal__action-cell elite-compatible-games-modal__action-cell--match" role="cell">' +
          '<button class="elite-compatible-games-modal__action" type="button" data-compatible-game-match="' + index + '">' +
            '<strong>Cheapest Match</strong>' +
            '<small>' + escapeHtml(match.priceLabel ? `from ${match.priceLabel}` : 'View build') + '</small>' +
          '</button>' +
        '</span>' +
      '</div>'
    );
  };

  const fallbackGameFromRow = (row, modal) => {
    if (!row || !modal) return null;

    const title = row.dataset.compatibleGameTitle
      || row.querySelector('.elite-compatible-games-modal__game strong')?.textContent?.trim()
      || 'Game';
    const performance = row.dataset.compatibleGamePerformance || '';
    const fpsLabel = row.dataset.compatibleGameFpsLabel || '';
    const image = row.querySelector('.elite-compatible-games-modal__game img')?.currentSrc
      || row.querySelector('.elite-compatible-games-modal__game img')?.src
      || '';
    const rawPrice = row.dataset.compatibleGamePriceLabel || '';
    const priceLabel = rawPrice.replace(/^from\s+/i, '').trim();
    const targetUrl = safeUrl(row.dataset.compatibleGameUrl, modal.dataset.productUrl || window.location.pathname);
    const resolution = /4k/i.test(performance) ? '4K' : /1080/i.test(performance) ? '1080p' : '1440p';
    const fpsNumber = Number((fpsLabel.match(/\d+/) || [])[0]);
    const fps = Number.isFinite(fpsNumber) ? { [resolution]: { High: fpsNumber } } : {};

    return {
      name: title,
      image,
      fps,
      cheapest_match: {
        title: modal.dataset.productTitle || 'ElitePCS Gaming PC',
        url: targetUrl,
        price_label: priceLabel || rawPrice.replace(/^from\s+/i, '').trim() || modal.dataset.productPrice || '',
        fps,
        configuration: {
          processor: modal.dataset.productCpu || '',
          graphics_card: modal.dataset.productGpu || '',
          memory: modal.dataset.productRam || ''
        },
        why: `This is the lowest-cost configuration currently available in the FPS data for ${title}.`
      }
    };
  };

  const matrixMarkup = (game) => {
    const axes = axesForGame(game);
    if (!axes.resolutions.length || !axes.settings.length) return '<p>No FPS matrix is available for this game yet.</p>';

    return (
      '<div class="elite-compatible-games-modal__fps-box">' +
        '<h4>FPS by resolution and settings</h4>' +
        '<div class="elite-compatible-games-modal__fps-table-wrap">' +
          '<table class="elite-compatible-games-modal__fps-table">' +
            '<thead><tr><th>Resolution</th>' +
              axes.settings.map((preset) => `<th>${escapeHtml(labelOf(preset))}</th>`).join('') +
            '</tr></thead><tbody>' +
              axes.resolutions.map((resolution) => (
                '<tr><th>' + escapeHtml(labelOf(resolution)) + '</th>' +
                  axes.settings.map((preset) => {
                    const value = fpsValue(game, resolution, preset);
                    return '<td>' + (value === null ? '&mdash;' : `${value} FPS`) + '</td>';
                  }).join('') +
                '</tr>'
              )).join('') +
            '</tbody></table>' +
        '</div>' +
      '</div>'
    );
  };

  const detailMarkup = (game, modal) => {
    const match = cheapestMatchForGame(game, modal);
    const featured = featuredFpsRow(fpsSource(match.fps));
    const summary = performanceSummary(featured);
    const vsCurrent = match.vsCurrent || {};
    const rawSaving = vsCurrent.saving_label || vsCurrent.savingLabel || '';
    const targetUrl = safeUrl(match.url, modal.dataset.productUrl || window.location.pathname);
    const normalizePath = (url) => {
      try {
        return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
      } catch (error) {
        return String(url || '').split('?')[0].replace(/\/$/, '');
      }
    };
    const isCurrentBuild = normalizePath(targetUrl) === normalizePath(modal.dataset.productUrl || window.location.pathname);
    const hasComparison = Boolean(vsCurrent.summary);
    const saving = isCurrentBuild ? '' : rawSaving;
    const priceLabel = isCurrentBuild && modal.dataset.productPrice
      ? modal.dataset.productPrice
      : (match.priceLabel || 'Price unavailable');
    const priceNote = saving
      ? saving
      : (isCurrentBuild ? 'Live price shown from this product page' : 'Matched product price from game data');
    const comparison = hasComparison
      ? vsCurrent.summary
      : (isCurrentBuild ? 'Current product page' : 'Price comparison pending');
    const comparisonLabel = hasComparison ? 'Vs your build' : (isCurrentBuild ? 'Matched page' : 'Fit check');
    const comparisonNote = hasComparison
      ? 'Checked against your current build and FPS data'
      : (isCurrentBuild ? 'Live store price shown; FPS data matched to this page' : 'Matched to FPS data; live price comparison is still catching up');
    const calloutLead = isCurrentBuild ? 'This product match delivers' : 'Lowest-priced matched configuration delivers';
    const performanceLine = summary.label + ', ' + summary.fps;
    const performanceNote = featured
      ? labelOf(featured.preset) + ' settings from compatible game data'
      : 'Compatible game data is available for this match';
    const primaryAction = isCurrentBuild
      ? '<button class="button button--primary" type="button" data-compatible-games-close>Keep this build selected</button>'
      : '<a class="button button--primary" href="' + escapeHtml(targetUrl) + '">View matched build</a>';
    const secondaryAction = isCurrentBuild
      ? '<button class="button button--secondary" type="button" data-compatible-game-detail-close>Back to games</button>'
      : '<a class="button button--secondary" href="' + escapeHtml(targetUrl) + '">Open full PC page</a>';
    const specs = [
      ['Processor', match.configuration.processor],
      ['Graphics Card', match.configuration.graphics_card],
      ['Memory (RAM)', match.configuration.memory],
      ['Storage', match.configuration.storage],
      ['Power Supply', match.configuration.power_supply],
      ['Cooling', match.configuration.cooling],
      ['Case', match.configuration.case],
      ['Operating System', match.configuration.operating_system]
    ].filter(([, value]) => value);

    return (
      '<div class="elite-compatible-games-modal__match-sheet">' +
        '<div class="elite-compatible-games-modal__detail-hero elite-compatible-games-modal__match-head">' +
          '<div class="elite-compatible-games-modal__match-media">' + gameImageMarkup(game, 160, modal) + '</div>' +
          '<div class="elite-compatible-games-modal__match-head-copy">' +
            '<span class="elite-compatible-games-modal__detail-eyebrow">Cheapest match</span>' +
            '<h3>' + escapeHtml(game.name || 'Game') + '</h3>' +
            '<p class="elite-compatible-games-modal__match-title">' + escapeHtml(match.title) + '</p>' +
            '<div class="elite-compatible-games-modal__detail-summary">' +
              '<span class="is-' + summary.tone + '">' + escapeHtml(summary.label) + '</span>' +
              '<strong>' + escapeHtml(summary.fps) + '</strong>' +
              (featured ? '<small>' + escapeHtml(labelOf(featured.preset)) + ' settings</small>' : '') +
            '</div>' +
          '</div>' +
        '</div>' +

        '<div class="elite-compatible-games-modal__match-verdict">' +
          '<span>Compatibility result</span>' +
          '<strong>' + escapeHtml(calloutLead) + ' <em>' + escapeHtml(performanceLine) + '</em></strong>' +
          '<small>' + escapeHtml(performanceNote) + '</small>' +
        '</div>' +

        '<div class="elite-compatible-games-modal__match-facts">' +
          '<div class="elite-compatible-games-modal__match-fact elite-compatible-games-modal__match-fact--price">' +
            '<span>Store price</span>' +
            '<strong>' + escapeHtml(priceLabel) + '</strong>' +
            '<small>' + escapeHtml(priceNote) + '</small>' +
          '</div>' +
          '<div class="elite-compatible-games-modal__match-fact elite-compatible-games-modal__match-fact--' + (hasComparison ? 'comparison' : (isCurrentBuild ? 'current' : 'caution')) + '">' +
            '<span>' + escapeHtml(comparisonLabel) + '</span>' +
            '<strong>' + escapeHtml(comparison) + '</strong>' +
            '<small>' + escapeHtml(comparisonNote) + '</small>' +
          '</div>' +
        '</div>' +

        '<div class="elite-compatible-games-modal__match-specs">' +
          '<h4>Build spec</h4>' +
          (specs.length ? specs.map(([label, value]) => (
            '<div class="elite-compatible-games-modal__match-spec">' +
              '<span>' + escapeHtml(label) + '</span>' +
              '<strong>' + escapeHtml(value) + '</strong>' +
            '</div>'
          )).join('') : '<p>Full configuration data is not available for this match yet.</p>') +
        '</div>' +

        '<div class="elite-compatible-games-modal__match-reason">' +
          '<strong>Why this build?</strong>' +
          '<p>' + escapeHtml(match.why) + '</p>' +
        '</div>' +

        '<div class="elite-compatible-games-modal__match-actions">' +
          primaryAction +
          secondaryAction +
        '</div>' +
      '</div>'
    );
  };

  const fpsDetailMarkup = (game, modal) => {
    const featured = featuredFpsRow(game);
    const summary = performanceSummary(featured);
    const cpu = modal.dataset.productCpu || '';
    const gpu = modal.dataset.productGpu || '';
    const ram = modal.dataset.productRam || '';

    return (
      '<div class="elite-compatible-games-modal__detail-hero">' +
        gameImageMarkup(game, 160, modal) +
        '<div>' +
          '<span class="elite-compatible-games-modal__detail-eyebrow">FPS info for</span>' +
          '<h3>' + escapeHtml(game.name || 'Game') + '</h3>' +
          '<p>Estimated performance for this current ElitePCS configuration.</p>' +
          '<div class="elite-compatible-games-modal__detail-summary">' +
            '<span class="is-' + summary.tone + '">' + escapeHtml(summary.label) + '</span>' +
            '<strong>' + escapeHtml(summary.fps) + '</strong>' +
            (featured ? '<small>' + escapeHtml(labelOf(featured.preset)) + ' settings</small>' : '') +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="elite-compatible-games-modal__detail-hardware">' +
        (gpu ? '<span><strong>GPU</strong>' + escapeHtml(gpu) + '</span>' : '') +
        (cpu ? '<span><strong>CPU</strong>' + escapeHtml(cpu) + '</span>' : '') +
        (ram ? '<span><strong>RAM</strong>' + escapeHtml(ram) + '</span>' : '') +
      '</div>' +
      matrixMarkup(game)
    );
  };

  const setCompatibleStatus = (modal, message, state = '') => {
    const status = modal.querySelector('[data-compatible-games-status]');
    if (!status) return;

    status.textContent = message || '';
    status.hidden = !message;
    status.dataset.status = state;
  };

  const rowSearchText = (row) => [
    row.dataset.compatibleGameSearch,
    row.dataset.compatibleGameTitle
  ].filter(Boolean).join(' ').toLowerCase();

  const filterCompatibleGames = (modal) => {
    if (!modal) return;

    const search = modal.querySelector('[data-compatible-games-search]');
    const query = (search?.value || '').trim().toLowerCase();
    let visibleCount = 0;

    modal.querySelectorAll('[data-compatible-game-row]').forEach((row) => {
      const isVisible = query === '' || rowSearchText(row).includes(query);
      row.hidden = !isVisible;
      row.classList.toggle('is-filtered', !isVisible);
      if (isVisible) visibleCount += 1;
    });

    modal.dataset.compatibleGamesVisibleCount = String(visibleCount);
  };

  const hydrateCompatibleGames = async (modal) => {
    if (!modal || modal.dataset.compatibleGamesLoaded === 'true' || modal.dataset.compatibleGamesLoading === 'true') return;

    const endpoint = endpointForHandle(modal.dataset.fpsEndpoint, modal.dataset.productHandle);
    if (!endpoint) return;

    modal.dataset.compatibleGamesLoading = 'true';
    setCompatibleStatus(modal, 'Loading live FPS data...', 'loading');

    try {
      const cacheKey = `elitepcs:compatible-games:v3:${modal.dataset.productHandle}:${endpoint}`;
      let data = readCachedJson(cacheKey, 5 * 60 * 1000);

      if (!data) {
        data = await fetch(endpoint, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'omit',
          cache: 'no-cache'
        }).then((response) => {
          if (!response.ok) throw new Error(`FPS endpoint returned ${response.status}`);
          return response.json();
        });
        writeCachedJson(cacheKey, data);
      }

      const games = Array.isArray(data.games) ? data.games.filter((game) => game && game.name && !isGameHidden(game) && allFpsRows(game).length) : [];
      if (!games.length) throw new Error('FPS endpoint returned no games for this product');

      modal._eliteFpsGames = games;
      modal.querySelectorAll('[data-compatible-game-row]').forEach((row) => row.remove());

      const table = modal.querySelector('.elite-compatible-games-modal__table');
      if (table) table.insertAdjacentHTML('beforeend', games.map((game, index) => gameRowMarkup(game, index, modal)).join(''));

      modal.dataset.compatibleGamesLoaded = 'true';
      filterCompatibleGames(modal);
      setCompatibleStatus(modal, '', 'success');
    } catch (error) {
      console.warn('ElitePCS live compatible games data could not be loaded. Showing fallback estimates.', error);
      setCompatibleStatus(modal, 'Live FPS data is unavailable for this build right now. Showing fallback estimates.', 'error');
    } finally {
      delete modal.dataset.compatibleGamesLoading;
    }
  };

  const openGameDetail = (modal, gameIndex, trigger, mode = 'match') => {
    let game = modal && modal._eliteFpsGames ? modal._eliteFpsGames[Number(gameIndex)] : null;
    if (!game && trigger) {
      game = fallbackGameFromRow(trigger.closest('[data-compatible-game-row]'), modal);
    }

    const detail = modal && modal.querySelector('[data-compatible-game-detail]');
    const content = modal && modal.querySelector('[data-compatible-game-detail-content]');
    if (!game || !detail || !content) return;

    modal._eliteDetailTrigger = trigger || document.activeElement;
    detail.dataset.detailMode = mode;
    content.innerHTML = mode === 'fps' ? fpsDetailMarkup(game, modal) : detailMarkup(game, modal);
    detail.hidden = false;

    const closeButton = detail.querySelector('[data-compatible-game-detail-close]');
    requestAnimationFrame(() => closeButton && closeButton.focus());
  };

  const closeGameDetail = (modal) => {
    const detail = modal && modal.querySelector('[data-compatible-game-detail]');
    const content = modal && modal.querySelector('[data-compatible-game-detail-content]');
    if (!detail) return;

    detail.hidden = true;
    delete detail.dataset.detailMode;
    if (content) content.innerHTML = '';

    if (modal._eliteDetailTrigger && typeof modal._eliteDetailTrigger.focus === 'function') {
      modal._eliteDetailTrigger.focus();
    }

    modal._eliteDetailTrigger = null;
  };

  const initTabs = (scope = document) => {
    scope.querySelectorAll('[data-product-tabs-section]').forEach((section) => {
      if (section.dataset.productTabsReady === 'true') return;
      section.dataset.productTabsReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-product-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-product-tab-panel]'));

      const activate = (tab) => {
        if (!tab || tab.hidden) return;
        tabs.forEach((item) => {
          const isActive = item === tab;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-selected', isActive ? 'true' : 'false');
          item.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === tab.getAttribute('aria-controls');
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      };

      // Layout changes can select a tab without scrolling or stealing focus.
      section.addEventListener('elite:product-tab-select', (event) => {
        activate(tabs.find((tab) => tab.dataset.s3Tab === event.detail?.key));
      });

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          const visibleTabs = tabs.filter((item) => !item.hidden);
          const index = visibleTabs.indexOf(tab);
          if (index < 0) return;
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % visibleTabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + visibleTabs.length) % visibleTabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = visibleTabs.length - 1;

          visibleTabs[nextIndex].focus();
          activate(visibleTabs[nextIndex]);
        });
      });
      section.dispatchEvent(new CustomEvent('elite:product-tabs-ready'));
    });
  };

  (() => {
    let activeModal = null;
    let previousFocus = null;

    const getFocusable = (modal) => Array.from(modal.querySelectorAll(focusableSelector)).filter((element) => element.offsetParent !== null);
    const isTouchModalViewport = () => {
      if (typeof window.matchMedia !== 'function') return false;
      return window.matchMedia('(max-width: 760px), (pointer: coarse)').matches;
    };

    const open = (targetId) => {
      if (config.pcsByGameVisible === false) return;

      const modal = targetId ? document.getElementById(targetId) : document.querySelector('[data-compatible-games-modal]');
      if (!modal) return;

      previousFocus = document.activeElement;
      activeModal = modal;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('elite-modal-open');
      document.body.classList.add('elite-modal-open');
      hydrateCompatibleGames(modal);

      const search = modal.querySelector('[data-compatible-games-search]');
      const dialog = modal.querySelector('[role="dialog"]');
      const closeButton = modal.querySelector('[data-compatible-games-close]');
      requestAnimationFrame(() => {
        const focusTarget = isTouchModalViewport()
          ? (closeButton || dialog || modal)
          : (search || dialog || modal);

        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      });
    };

    const close = (modal = activeModal) => {
      if (!modal) return;

      closeGameDetail(modal);
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('elite-modal-open');
      document.body.classList.remove('elite-modal-open');

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }

      activeModal = null;
      previousFocus = null;
    };

    const compatibleActionSelector = [
      '[data-compatible-games-open]',
      '[data-compatible-game-match]',
      '[data-compatible-game-fps]',
      '[data-compatible-game-detail-close]',
      '[data-compatible-games-close]'
    ].join(',');
    let suppressNextTouchClick = false;

    const handleCompatibleAction = (event) => {
      if (!(event.target instanceof Element)) return false;

      const openButton = event.target.closest('[data-compatible-games-open]');
      if (openButton) {
        event.preventDefault();
        open(openButton.dataset.compatibleGamesOpen);
        return true;
      }

      const detailButton = event.target.closest('[data-compatible-game-match]');
      if (detailButton) {
        event.preventDefault();
        openGameDetail(detailButton.closest('[data-compatible-games-modal]'), detailButton.dataset.compatibleGameMatch, detailButton);
        return true;
      }

      const fpsButton = event.target.closest('[data-compatible-game-fps]');
      if (fpsButton) {
        event.preventDefault();
        openGameDetail(fpsButton.closest('[data-compatible-games-modal]'), fpsButton.dataset.compatibleGameFps, fpsButton, 'fps');
        return true;
      }

      const detailClose = event.target.closest('[data-compatible-game-detail-close]');
      if (detailClose) {
        event.preventDefault();
        closeGameDetail(detailClose.closest('[data-compatible-games-modal]'));
        return true;
      }

      if (event.target.closest('[data-compatible-games-close]')) {
        event.preventDefault();
        close(event.target.closest('[data-compatible-games-modal]') || activeModal);
        return true;
      }

      return false;
    };

    document.addEventListener('pointerup', (event) => {
      if (!event.target.closest || !event.target.closest(compatibleActionSelector)) return;
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

      suppressNextTouchClick = true;
      handleCompatibleAction(event);
      window.setTimeout(() => {
        suppressNextTouchClick = false;
      }, 450);
    });

    document.addEventListener('touchend', (event) => {
      if (suppressNextTouchClick || !event.target.closest || !event.target.closest(compatibleActionSelector)) return;

      suppressNextTouchClick = true;
      handleCompatibleAction(event);
      window.setTimeout(() => {
        suppressNextTouchClick = false;
      }, 450);
    }, { passive: false });

    document.addEventListener('click', (event) => {
      if (suppressNextTouchClick && event.target.closest && event.target.closest(compatibleActionSelector)) {
        event.preventDefault();
        return;
      }

      handleCompatibleAction(event);
    });

    document.addEventListener('keydown', (event) => {
      if (!activeModal) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        const detail = activeModal.querySelector('[data-compatible-game-detail]');
        if (detail && !detail.hidden) closeGameDetail(activeModal);
        else close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(activeModal);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('input', (event) => {
      if (!(event.target instanceof Element)) return;

      const search = event.target.closest('[data-compatible-games-search]');
      if (!search) return;

      filterCompatibleGames(search.closest('[data-compatible-games-modal]'));
    });

    document.addEventListener('error', (event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement) || !image.matches('[data-compatible-game-image]')) return;

      let fallbacks = [];

      try {
        fallbacks = JSON.parse(image.dataset.imageFallbacks || '[]');
      } catch {
        fallbacks = [];
      }

      const nextImage = fallbacks.shift();
      if (nextImage) {
        image.dataset.imageFallbacks = JSON.stringify(fallbacks);
        image.src = nextImage;
        return;
      }

      const placeholder = document.createElement('span');
      placeholder.className = 'elite-compatible-games-modal__game-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      image.replaceWith(placeholder);
    }, true);
  })();

  document.addEventListener('DOMContentLoaded', () => initTabs());
  document.addEventListener('shopify:section:load', (event) => initTabs(event.target));
})();
